import { API_BASE_URL } from "./constants";
import { buildCacheKey, getCachedResponse, setCachedResponse, clearCacheByPattern } from "@/lib/offline/cache";

// Import CACHE_PREFIX for direct localStorage access
const CACHE_PREFIX = "alraheem:cache:";

import { enqueueRequest } from "@/lib/offline/queue";
import { addPendingRecord, formDataToJson } from "@/lib/offline/pendingRecords";

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: any[];
  fromCache?: boolean;
  queuedOffline?: boolean;
}

class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errors?: any[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const DEFAULT_CACHE_TTL = 1000 * 60 * 5; // 5 minutes
const isBrowser = typeof window !== "undefined";

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }

  return { ...(headers as Record<string, string>) };
};

interface ExtendedRequestInit extends RequestInit {
  cacheKey?: string;
  cacheTTL?: number;
  skipOffline?: boolean;
  queueOffline?: boolean;
  skipCache?: boolean;
  invalidateCache?: string | string[]; // Pattern(s) to invalidate after mutation
  recordType?: "goat" | "feedPurchase" | "expense" | "booking"; // Type of record for pending storage
}

const canPersistBody = (body: BodyInit | null | undefined) => {
  return typeof body === "string";
};

const getCacheIdentifier = (method: string, endpoint: string, body?: BodyInit | null) => {
  const serializedBody = typeof body === "string" ? body : null;
  return buildCacheKey(method, endpoint, serializedBody);
};

export const apiRequest = async <T = any>(
  endpoint: string,
  options: ExtendedRequestInit = {},
): Promise<ApiResponse<T>> => {
  const token = isBrowser ? localStorage.getItem("token") : null;
  const {
    cacheKey,
    cacheTTL = DEFAULT_CACHE_TTL,
    skipOffline = false,
    queueOffline = true,
    skipCache = false,
    invalidateCache,
    recordType,
    ...rest
  } = options;

  const headerRecord: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...normalizeHeaders(rest.headers),
  };

  const bodyIsFormData = typeof FormData !== "undefined" && rest.body instanceof FormData;
  if (bodyIsFormData) {
    delete headerRecord["Content-Type"];
  }

  const config: RequestInit = {
    ...rest,
    headers: headerRecord,
  };

  const method = (config.method ?? "GET").toUpperCase();
  const cacheIdentifier = cacheKey ?? getCacheIdentifier(method, endpoint, config.body);

  const respondFromCache = () => {
    if (!isBrowser || skipCache) {
      return null;
    }
    const cached = getCachedResponse<ApiResponse<T>>(cacheIdentifier);
    if (cached) {
      // Don't return cached empty arrays if they're older than 10 seconds
      // This prevents stale empty arrays from being returned
      if (Array.isArray(cached.data) && cached.data.length === 0) {
        // For empty arrays, check cache age from localStorage directly
        try {
          const raw = localStorage.getItem(`${CACHE_PREFIX}${cacheIdentifier}`);
          if (raw) {
            const entry = JSON.parse(raw);
            const cacheAge = Date.now() - entry.timestamp;
            if (cacheAge > 10000) {
              // Cache is older than 10 seconds, skip it
              localStorage.removeItem(`${CACHE_PREFIX}${cacheIdentifier}`);
              return null;
            }
          }
        } catch {
          // If we can't check age, skip the cache
          return null;
        }
      }
      return { ...cached, fromCache: true };
    }
    return null;
  };

  const handleOfflineMutation = async () => {
    if (!queueOffline) {
      throw new ApiError("Offline. Unable to complete request without network access.");
    }

    // Handle FormData (for goat creation with images)
    let bodyString: string | undefined;
    let imageData: string | undefined;
    let recordData: any = {};

    if (bodyIsFormData && config.body instanceof FormData) {
      // Convert FormData to JSON for storage
      const { data, imageData: imgData } = await formDataToJson(config.body as FormData);
      recordData = data;
      imageData = imgData;
      bodyString = JSON.stringify(data);
    } else if (canPersistBody(config.body)) {
      bodyString = config.body as string;
      try {
        recordData = JSON.parse(bodyString);
      } catch {
        // If parsing fails, use empty object
      }
    } else {
      throw new ApiError("Offline. Unable to complete request without network access.");
    }

    // Queue the request for sync
    enqueueRequest({
      endpoint,
      method,
      body: bodyString,
      headers: headerRecord,
    });

    // If this is a record type we track, save it as pending
    if (recordType && method === "POST") {
      try {
        const pendingRecord = addPendingRecord(
          recordType,
          recordData,
          endpoint,
          method,
          bodyString,
          headerRecord,
          imageData
        );

        // Return the pending record immediately so it shows in UI
        return {
          success: true,
          message: "Record saved offline. It will sync automatically when you're back online.",
          queuedOffline: true,
          data: pendingRecord.data as T, // Return the pending record data
        } as ApiResponse<T>;
      } catch (error: any) {
        // If duplicate detected, check if there's already a pending record
        if (error.message?.includes("Duplicate")) {
          // Return success but indicate it's already queued
          return {
            success: true,
            message: "Record already queued for sync.",
            queuedOffline: true,
          } as ApiResponse<T>;
        }
        throw error;
      }
    }

    return {
      success: true,
      message: "Action saved offline. It will sync automatically when you're back online.",
      queuedOffline: true,
    } as ApiResponse<T>;
  };

  if (!skipOffline && isBrowser && !navigator.onLine) {
    if (method === "GET") {
      const cached = respondFromCache();
      if (cached) {
        return cached;
      }
      throw new ApiError("Offline. Cached data is not available for this request.");
    }
    return await handleOfflineMutation();
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  } catch (error) {
    if (method === "GET") {
      const cached = respondFromCache();
      if (cached) {
        return cached;
      }
    } else if (!skipOffline && isBrowser && !navigator.onLine) {
      return await handleOfflineMutation();
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError("Network error. Please check your connection.");
  }

  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch {
    data = { success: response.ok } as ApiResponse<T>;
  }

  if (!response.ok) {
    if (response.status === 401) {
      if (isBrowser) {
        localStorage.removeItem("token");
        window.location.href = "/auth";
      }
    }
    throw new ApiError(data.message || "An error occurred", response.status, data.errors);
  }

  // Invalidate cache after mutations (POST, PUT, DELETE, PATCH)
  if (invalidateCache && isBrowser) {
    const patterns = Array.isArray(invalidateCache) ? invalidateCache : [invalidateCache];
    patterns.forEach((pattern) => {
      clearCacheByPattern(pattern);
    });
  }

  // Cache GET responses, but don't cache empty arrays for too long
  if (method === "GET" && data.success && isBrowser && !skipCache) {
    // If data is an empty array, use shorter TTL (30 seconds)
    // Otherwise use the provided TTL
    const finalTTL = Array.isArray(data.data) && data.data.length === 0 
      ? Math.min(30000, cacheTTL) // Max 30 seconds for empty arrays
      : cacheTTL;
    
    setCachedResponse(cacheIdentifier, data, finalTTL);
  }

  return data;
};

export default apiRequest;

